import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaTree,
  FaSeedling,
  FaGlobeAmericas,
  FaUsers,
  FaLeaf,
  FaHome,
  FaRegHeart,
} from "react-icons/fa";
import "./Forestry.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const HERO_FEATURES = [
  { icon: FaTree, label: "Sustainable Practices" },
  { icon: FaSeedling, label: "Protecting Nature" },
  { icon: FaGlobeAmericas, label: "Environmental Conservation" },
  { icon: FaUsers, label: "Stronger Communities" },
];

const STATS = [
  { icon: FaTree, value: "10K+", label: "Trees Planted" },
  { icon: FaUsers, value: "50+", label: "Villages Covered" },
  { icon: FaSeedling, value: "25+", label: "Years of Impact" },
  { icon: FaGlobeAmericas, value: "Better", label: "Environment" },
];

const PROGRAMS = [
  {
    icon: FaLeaf,
    title: "Energy Plantation",
    text: "Promoting plantation drives for a greener and sustainable future.",
  },
  {
    icon: FaTree,
    title: "Forest Plantation",
    text: "Increasing green cover through large scale afforestation programs.",
  },
  {
    icon: FaUsers,
    title: "Promotion of Vana Samrakshana Samiti",
    text: "(Forest Protection Committees) Empowering communities to protect and conserve forests.",
  },
  {
    icon: FaHome,
    title: "Village Plantation Programs",
    text: "Encouraging village participation in plantation and forest restoration activities.",
  },
];

const Forestry = () => {
  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* ================= Hero (1521 x 482, with icon feature row) ================= */}
      <section className="fs-page">
        <div className="fs-container">
          <motion.div
            className="fs-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="fs-hero__image"
              style={{ backgroundImage: "url(/assets/images/backgrounds/Forestry.png)" }}
            />
            <div className="fs-hero__overlay" />
            <svg className="fs-hero__wave" viewBox="0 0 1521 90" preserveAspectRatio="none">
              <path d="M0,0 C400,90 1121,90 1521,0 L1521,90 L0,90 Z" fill="#ffffff" />
            </svg>

            <div className="fs-hero__content">
              <div className="fs-hero__brand">
                <span className="fs-hero__badge" />
                <span className="fs-hero__brandLabel">Vikash Foundation</span>
              </div>

              <motion.h2
                className="fs-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Forestry
              </motion.h2>

              <span className="fs-hero__dash">
                <FaLeaf />
              </span>

              <motion.p
                className="fs-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Vikash supports forestry initiatives by promoting sustainable
                practices, protecting natural resources, and enhancing
                environmental conservation efforts.
              </motion.p>

              <div className="fs-hero__iconRow">
                {HERO_FEATURES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div className="fs-hero__iconItem" key={f.label}>
                      <span className="fs-hero__iconCircle">
                        <Icon />
                      </span>
                      <span className="fs-hero__iconLabel">{f.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Introduction ================= */}
      <section className="fs-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-start g-5">
              <motion.div className="col-lg-5" variants={staggerItem}>
                <span className="fs-eyebrow">
                  <FaLeaf /> Our Purpose
                </span>
                <h2 className="fs-heading">Protecting a Vital Ecosystem</h2>
                <span className="fs-heading__underline">
                  <FaLeaf />
                </span>
                <p className="fs-intro__text">
                  Forests play a crucial role in human habitat, supporting
                  livelihoods and mitigating climate change by acting as a
                  carbon sink. Recognizing their importance, Vikash undertakes
                  extensive plantation and conservation efforts across various
                  parts of Odisha to protect these vital natural resources for
                  future generations.
                </p>
              </motion.div>

              <motion.div className="col-lg-7" variants={staggerItem}>
                <img
                  src="/assets/images/Subsections/f1.jpg"
                  alt="Lush green forest"
                  className="fs-intro__image"
                />
              </motion.div>
            </Row>

            <motion.div className="fs-statsRow" variants={staggerItem}>
              {STATS.map((s) => {
                const Icon = s.icon;
                return (
                  <div className="fs-statsRow__item" key={s.label}>
                    <span className="fs-statsRow__icon">
                      <Icon />
                    </span>
                    <div>
                      <div className="fs-statsRow__value">{s.value}</div>
                      <div className="fs-statsRow__label">{s.label}</div>
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* ================= Programs ================= */}
      <section className="fs-approach">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="fs-approach__header"
          >
            <span className="fs-eyebrow fs-eyebrow--center">
              <FaLeaf /> Our Forestry Conservation Programs
            </span>
            <h2 className="fs-heading fs-heading--center">
              Our Forestry Conservation Programs
            </h2>
            <p className="fs-approach__lead">
              We focus on community-led initiatives to ensure sustainable
              forest management.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {PROGRAMS.map((p) => {
                const Icon = p.icon;
                return (
                  <Col md={6} lg={3} key={p.title}>
                    <motion.div variants={staggerItem} className="fs-card">
                      <span className="fs-card__icon">
                        <Icon />
                      </span>
                      <h5 className="fs-card__title">{p.title}</h5>
                      <p className="fs-card__text">{p.text}</p>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="fs-cta">
        <Container>
          <motion.div
            className="fs-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div
              className="fs-cta__bg"
              style={{ backgroundImage: "url(/assets/images/sections/sapling-hands.jpg)" }}
            />
            <div className="fs-cta__overlay" />

            <div className="fs-cta__content">
              <span className="fs-eyebrow fs-eyebrow--light">
                <FaLeaf /> Join Our Mission
              </span>
              <h2 className="fs-cta__title">Help Protect Our Forests</h2>
              <span className="fs-heading__underline fs-heading__underline--light">
                <FaLeaf />
              </span>
              <p className="fs-cta__text">
                Your contribution helps us plant more trees and support
                communities in conserving their local forest resources.
              </p>
              <div className="fs-cta__actions">
                <button
                  type="button"
                  className="fs-btn fs-btn--solid"
                  onClick={openDonation}
                >
                  <FaRegHeart /> Donate
                </button>
                <button
                  type="button"
                  className="fs-btn fs-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default Forestry;